import React from "react";
import { MemoryRouter, useLocation } from "react-router-dom";
import {
  XMarkIcon,
  PlusIcon,
  ArrowPathIcon,
  Squares2X2Icon,
} from "@heroicons/react/24/outline";

import { useTabs } from "./TabContext";
import AppRoutes from "../AppRoutes";
import TakeoffPage from "./TakeoffPage";

import { TabRefreshContext } from "./TabRefreshContext";

function LocationSync({ tabId, onChange }) {
  const location = useLocation();
  React.useEffect(() => {
    const path = location.pathname + location.search;
    onChange(tabId, path, location.state || {});
  }, [tabId, location.pathname, location.search, location.state, onChange]);
  return null;
}

export default function TabbedWorkspace({ user, companyProfile }) {
  const {
    tabs,
    activeId,
    openTab,
    closeTab,
    closeAllTabs,
    focusTab,
    refreshTab,
    updateTabLocation,
    reorderTabs,
  } = useTabs();

  // Drag-and-drop reorder state
  const [draggingId, setDraggingId] = React.useState(null);
  const [dragOverId, setDragOverId] = React.useState(null);

  const handleDragStart = (e, tabId) => {
    setDraggingId(tabId);
    e.dataTransfer.effectAllowed = "move";
    // Firefox requires data to be set for drag to initiate
    e.dataTransfer.setData("text/plain", tabId);
  };

  const handleDragOver = (e, tabId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (tabId !== dragOverId) setDragOverId(tabId);
  };

  const handleDrop = (e, tabId) => {
    e.preventDefault();
    if (draggingId && draggingId !== tabId) {
      reorderTabs(draggingId, tabId);
    }
    setDraggingId(null);
    setDragOverId(null);
  };

  const handleDragEnd = () => {
    setDraggingId(null);
    setDragOverId(null);
  };

  return (
    <div className="flex flex-col">
      {/* Tab bar */}
      <div className="sticky mt-2 mx-1 sm:mx-4 px-1 sm:px-2 rounded-lg py-1.5 z-[9] flex items-stretch gap-1 sm:gap-1.5 bg-gray-900 border-b border-gray-700/60 overflow-x-auto scrollbar-thin">
        {tabs.map((tab) => {
          const active = tab.id === activeId;
          const isDragging = tab.id === draggingId;
          const isDragOver = tab.id === dragOverId && tab.id !== draggingId;
          return (
            <div
              key={tab.id}
              draggable
              onDragStart={(e) => handleDragStart(e, tab.id)}
              onDragOver={(e) => handleDragOver(e, tab.id)}
              onDrop={(e) => handleDrop(e, tab.id)}
              onDragEnd={handleDragEnd}
              onClick={() => focusTab(tab.id)}
              className={
                "group relative flex items-center gap-2 rounded-lg pl-3.5 pr-2 py-2 text-sm font-medium cursor-pointer whitespace-nowrap transition-all duration-150 " +
                (active
                  ? "bg-gray-700 text-white shadow-sm ring-1 ring-orange-400/40"
                  : "text-gray-400 hover:text-gray-100 hover:bg-gray-800") +
                (isDragging ? " opacity-40" : "") +
                (isDragOver ? " ring-2 ring-orange-400" : "")
              }
              title={tab.label}
            >
              {/* active accent dot / dirty indicator */}
              <span
                className={
                  "h-1.5 w-1.5 rounded-full transition-colors shrink-0 " +
                  (tab.dirty
                    ? "bg-orange-400 animate-pulse"
                    : active
                      ? "bg-orange-400"
                      : "bg-transparent group-hover:bg-gray-600")
                }
                title={tab.dirty ? "Unsaved changes" : undefined}
              />
              <span className="max-w-[100px] sm:max-w-[160px] truncate text-xs sm:text-sm">
                {tab.label}
                {tab.dirty && <span className="text-orange-400"> •</span>}
              </span>

              {tabs.length > 1 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    closeTab(tab.id);
                  }}
                  className={
                    "ml-0.5 rounded-md p-1 text-gray-500 transition-colors hover:bg-gray-600 hover:text-white " +
                    (active
                      ? "opacity-100"
                      : "opacity-0 group-hover:opacity-100")
                  }
                  title="Close tab"
                >
                  <XMarkIcon className="h-3.5 w-3.5" />
                </button>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  refreshTab(tab.id);
                }}
                className={
                  "ml-0.5 rounded-md p-1 text-gray-500 transition-colors hover:bg-gray-600 hover:text-white " +
                  (active ? "opacity-100" : "opacity-0 group-hover:opacity-100")
                }
                title="Refresh tab"
              >
                <ArrowPathIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          );
        })}

        {/* divider */}
        <div className="mx-1 my-2 w-px shrink-0 bg-gray-700/60" />

        <button
          onClick={() => {
            const current = tabs.find((t) => t.id === activeId);
            openTab(current?.path || "/Projects", current?.state || {}, {
              focusExisting: false,
            });
          }}
          className="flex shrink-0 items-center justify-center rounded-lg px-2.5 my-1 text-gray-400 hover:bg-gray-800 hover:text-orange-400 transition-colors"
          title="New tab"
        >
          <PlusIcon className="h-4 w-4" />
        </button>

        {tabs.length > 1 && (
          <button
            onClick={() => closeAllTabs(activeId)}
            className="flex shrink-0 items-center justify-center gap-1 rounded-lg px-2.5 my-1 text-gray-400 hover:bg-gray-800 hover:text-red-400 transition-colors text-xs font-medium"
            title="Close all other tabs"
          >
            <Squares2X2Icon className="h-4 w-4" />
            <span className="hidden sm:inline">Close all</span>
          </button>
        )}
      </div>

      {/* All tabs mounted; only the active one is visible */}
      <div className="relative">
        {tabs.map((tab) => {
          const isActive = tab.id === activeId;
          return (
            <div key={tab.id} style={{ display: isActive ? "block" : "none" }}>
              <TabRefreshContext.Provider
                value={{ focusKey: tab.focusKey || 0, tabId: tab.id }}
              >
                <MemoryRouter
                  key={tab.focusKey}
                  initialEntries={[
                    {
                      pathname: tab.path.split("?")[0],
                      search: tab.path.includes("?")
                        ? "?" + tab.path.split("?")[1]
                        : "",
                      state: tab.state,
                    },
                  ]}
                >
                  <LocationSync tabId={tab.id} onChange={updateTabLocation} />
                  <AppRoutes user={user} companyProfile={companyProfile} />
                </MemoryRouter>
              </TabRefreshContext.Provider>
            </div>
          );
        })}
      </div>
    </div>
  );
}
